import { ScrollView, View } from "@-/com.npmjs.react-native";
import { useLocalSearchParams, useRouter } from "@-/com.npmjs.expo-router";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { BlockAuthForm } from "@-/dev.improve.haniverse.routes.nav.app.auth._components.block-auth-form";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useHasHydrated } from "@-/dev.improve.haniverse.lib.hydration";
import { ScreenReadyMarker } from "@-/dev.improve.haniverse.components.ui.screen-ready-marker";
import { useTheme } from "@-/dev.improve.haniverse.lib.theme";
import { AuthWelcomeGalaxy } from "#auth-welcome-galaxy";
import { welcomeGalaxyPalette } from "#welcome-galaxy-palette";

/**
 * The fresh-install welcome gate, in the app's own theme so that entering the app never switches
 * between dark and light: in dark mode the galaxy is additive light on deep space, and in light
 * mode ink on off-white paper (see `welcome-galaxy-palette.ts`).
 */
export function AuthPage() {
  const theme = useTheme();
  // The server can't know the browser's colour scheme and renders light. React keeps a mismatched
  // server `className` rather than patching it, so a dark browser would keep the light page class
  // and the welcome card's light ScopedTheme under a white wordmark. Hydrate light, then switch.
  const appearance = useHasHydrated() ? theme : "light";
  const { reason } = useLocalSearchParams<{ reason?: string }>();
  const router = useRouter();
  const [email] = useDeviceSetting("accountEmail");
  if (reason === "session-expired") {
    return (
      <ScrollView
        className="flex-1 bg-background"
        contentContainerClassName="grow justify-center gap-6 p-6"
        keyboardShouldPersistTaps="handled"
      >
        <ScreenReadyMarker screen="auth" />
        <View accessibilityRole="alert" className="gap-2">
          <Text className="text-2xl font-semibold">Session expired</Text>
          <Text className="text-muted-foreground">Please sign in again to continue.</Text>
        </View>
        <BlockAuthForm
          initialEmail={typeof email === "string" ? email : ""}
          initialMode="signIn"
          onSignedIn={() => {
            // eslint-disable-next-line han-no-router-navigation/no-router-navigation -- Recovery runs outside NavContext; startup prepares no database queries.
            router.replace("/(nav)/startup");
          }}
          requireFreshSession
        />
      </ScrollView>
    );
  }
  return (
    <View className={`flex-1 ${welcomeGalaxyPalette(appearance).pageClassName}`}>
      <ScreenReadyMarker screen="auth" />
      <AuthWelcomeGalaxy appearance={appearance} />
    </View>
  );
}
